import { h, Component } from 'preact';
import { showModal } from 'utils/modal';
import { canUser } from 'utils/roles';

import Button from 'components/button/button';
import SimpleField from 'components/editor/settings/simpleField';
import DropdownField from 'components/editor/settings/dropdownField';

import s from 'components/main/navigation/editLink.sss';

const targetTypeNames = {
	none: 'None (text only)',
	external: 'External link',
	page: 'Page',
	cookies: 'Cookie consent',
};

const COOKIE_LINK_TEXT = 'Your Privacy Choices / Cookie Notice';

class EditLink extends Component {

	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.onNameInput = this.onNameInput.bind(this);
		this.onTypeChange = this.onTypeChange.bind(this);
	}

	componentDidMount() {
		const link = this.props.link;
		if (!link) return;
		this.previousName = link.name;
		this.updateInput('name', link.name);

		const targetTypes = this.props.targetTypes || [];
		if (targetTypes.includes('cookies') && link.destination === 'cookies') {
			this.updateInput('type', 'cookies');
		} else if (targetTypes.includes('page') && this.pages.some(page => page.id === link.destination)) {
			this.updateInput('type', 'page');
			this.updateInput('page', link.destination);
		} else if (targetTypes.includes('external') && link.destination) {
			this.updateInput('type', 'external');
			this.updateInput('link', link.destination);
		} else if (targetTypes.includes('none')) {
			this.updateInput('type', 'none');
		}

		this.updateNamePreview();
	}

	get pages() {
		return this.props.pages || [];
	}

	updateInput(name, value) {
		const element = this.base.querySelector('form')?.elements[name];
		if (element) {
			element.value = value || '';
			const input = element instanceof RadioNodeList ? [...element].find(e => e.checked) || element[0] : element;
			input?.dispatchEvent(new Event('change'));
		}
	}

	updateNamePreview() {
		if (!this.props.namePreview) return;
		const name = this.base.querySelector('form')?.elements.name;
		const replacements = Object.entries(this.props.namePreview.replacements || {});
		const output = replacements.reduce(
			(value, [key, replacement]) => value.replaceAll(new RegExp(RegExp.escape(key), 'gi'), replacement),
			name?.value || ''
		);
		// this.updateInput('name-preview', output);
		// Setting the value works, but might not persist if something else causes a state change, so we use the state instead
		this.setState({ namePreview: output });
	}

	onNameInput(e) {
		this.previousName = e.target.value;
		this.updateNamePreview();
	}

	onTypeChange(e) {
		const name = this.base.querySelector('form')?.elements.name.value;
		if (e.target.value === 'cookies') {
			this.previousName = name;
			this.updateInput('name', COOKIE_LINK_TEXT);
		} else {
			this.updateInput('name', this.previousName || '');
		}
		this.setState({ type: e.target.value });
		this.updateNamePreview();
	}

	getName(data) {
		if (data.type === 'cookies') return COOKIE_LINK_TEXT;
		return data.name;
	}

	getDestination(data) {
		if (data.type === 'none') return '';
		if (data.type === 'cookies') return 'cookies';
		if (data.type === 'page') return data.page;
		if (data.type === 'external') return data.link;
	}

	onSubmit(e) {
		if (e.submitter?.value === 'cancel') {
			return this.base.hidePopover();
		}

		if (e.submitter?.value === 'delete') {
			const response = { type: 'DELETE' };
			this.base.returnValue = JSON.stringify(response);
			return this.base.hidePopover();
		}

		const data = Object.fromEntries(new FormData(this.base.querySelector('form')));

		const returnValue = { name: this.getName(data), destination: this.getDestination(data) };
		this.base.returnValue = JSON.stringify(returnValue);
		this.base.hidePopover();
	}

	renderEditGroup() {
		const hasName = !Array.isArray(this.props.link);
		const readonly = !canUser('write');
		return (
			<form method="dialog" onSubmit={this.onSubmit}>
				<h3 class={s.title}>Edit group</h3>
				{!!hasName && <SimpleField class={s.nameField} label="Group name" name="name" required readonly={readonly} />}

				<div class={s.buttons}>
					{!!hasName && <Button primary class={s.button} type="submit" disabled={readonly}>Save</Button>}
					<Button outline class={s.button} type="submit" value="cancel">Cancel</Button>
					{!!this.props.deletable && <Button outline danger icon="delete" class={s.button} type="submit" value="delete" disabled={readonly}>Delete group</Button>}
				</div>
			</form>
		);
	}

	renderEditLink() {
		const readonly = !canUser('write');
		const { deletable, namePreview, targetTypes = [] } = this.props;
		const isCookies = this.state.type === 'cookies';
		const pages = this.pages;

		return (
			<form method="dialog" onSubmit={this.onSubmit}>
				<h3 class={s.title}>Edit link</h3>
				<SimpleField class={s.nameField} label="Link text" name="name" required disabled={readonly || isCookies} autocomplete="off" onInput={this.onNameInput} help={isCookies && 'Text will change automatically depending on the user region.'} />
				{namePreview && (
					<label>
						<div class={s.label}>Preview</div>
						<output name="name-preview">{this.state.namePreview}</output>
					</label>
				)}
				<DropdownField class={s.typeField} label="Target" name="type" disabled={readonly} key="main" onChange={this.onTypeChange}>
					{targetTypes.map(type => <option value={type}>{targetTypeNames[type] || type}</option>)}
				</DropdownField>
				{!!targetTypes.includes('external') && (
					<SimpleField class={s.externalLink} name="link" placeholder="https://" disabled={readonly} autocomplete="url" icon="link" />
				)}
				{!!targetTypes.includes('page') && (
					<DropdownField class={s.pageDropdown} name="page" icon="file" disabled={readonly}>
						{pages.map(page => <option value={page.id}>{page.name}</option>)}
					</DropdownField>
				)}
				<div class={s.buttons}>
					<Button primary class={s.button} type="submit" disabled={readonly}>Save</Button>
					<Button outline class={s.button} type="submit" value="cancel">Cancel</Button>
					{!!deletable && <Button outline danger icon="delete" class={s.button} type="submit" value="delete" disabled={readonly}>Delete link</Button>}
				</div>
			</form>
		);
	}

	render(props) {
		const isGroup = props.link?.sublinks || Array.isArray(props.link);
		let content = isGroup ? this.renderEditGroup() : this.renderEditLink();
		return (
			<dialog class={s.modal} style={`--anchor-name: ${props.anchor || 'normal'};`} popover="auto">
				{content}
			</dialog>
		);
	}
}

export async function showEditLinkModal(options = {}) {
	try {
		return JSON.parse(await showModal(EditLink, options));
	} catch {
		return null;
	}
}